const DEFAULT_INTERVAL_MS = 100;
const DEFAULT_MIN_RMS = 0.006;
const DEFAULT_MIN_PEAK = 0.02;
const DEFAULT_MIN_ACTIVE_MS = 500;

/**
 * Locally measures whether a stream contains sustained audible input.
 * Nothing is uploaded or persisted. A brief click/noise spike cannot satisfy
 * the test because both peak level and sustained active duration are required.
 */
export function startAudioQualityMonitor(stream, options = {}) {
  if (typeof window === "undefined" || !stream) return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  const intervalMs = options.intervalMs || DEFAULT_INTERVAL_MS;
  const minRms = options.minRms || DEFAULT_MIN_RMS;
  const minPeak = options.minPeak || DEFAULT_MIN_PEAK;
  const minActiveMs = options.minActiveMs || DEFAULT_MIN_ACTIVE_MS;
  try {
    const context = new AudioContextClass();
    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    const source = context.createMediaStreamSource(stream);
    source.connect(analyser);
    void context.resume().catch(() => undefined);
    const samples = new Float32Array(analyser.fftSize);
    let sampleCount = 0;
    let activeSamples = 0;
    let maxPeak = 0;
    let maxRms = 0;
    let stopped = false;

    const timer = window.setInterval(() => {
      analyser.getFloatTimeDomainData(samples);
      let peak = 0;
      let energy = 0;
      for (const sample of samples) {
        const absolute = Math.abs(sample);
        peak = Math.max(peak, absolute);
        energy += sample * sample;
      }
      const rms = Math.sqrt(energy / samples.length);
      sampleCount += 1;
      if (rms >= minRms) activeSamples += 1;
      maxPeak = Math.max(maxPeak, peak);
      maxRms = Math.max(maxRms, rms);
    }, intervalMs);

    const snapshot = () => {
      const activeMs = activeSamples * intervalMs;
      return {
        analysisAvailable: true,
        sampleCount,
        activeMs,
        maxPeak,
        maxRms,
        passed:
          sampleCount >= 3 &&
          activeMs >= minActiveMs &&
          maxPeak >= minPeak &&
          maxRms >= minRms,
      };
    };

    return {
      snapshot,
      stop() {
        if (!stopped) {
          stopped = true;
          window.clearInterval(timer);
          try {
            source.disconnect();
            analyser.disconnect();
            void context.close();
          } catch {
            // The browser may already have released the stream.
          }
        }
        return snapshot();
      },
    };
  } catch {
    return null;
  }
}
